// src/components/MyPurchasesDrawer.jsx
import { useState, useEffect } from "react";
import { X, Copy, CheckCheck, Package, RefreshCw, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import api from "../api/api";
import { toast } from "react-toastify";
import Currency from "./Currency";
import { formatShopviaCredential, credentialRowsToText } from "../utils/credentialFormat";

const CATEGORY_ICONS = {
  facebook:    "🔵",
  instagram:   "🟣",
  tiktok:      "⚫",
  twitter:     "🐦",
  telegram:    "✈️",
  vpn:         "🔒",
  texting_app: "💬",
  other:       "📱",
};

// ── buildCredentialLine ───────────────────────────────────────────────────────
// Works identically for DB and VaultAcc purchases.
//
// DB purchases:      credentials.email + password + extra → "email:password:extra"
// VaultAcc purchases: full log content in credentials.email, password/extra null
//                    → the raw log string is used as-is
//
// Both display in the same monospace credential box — indistinguishable to user.
// ─────────────────────────────────────────────────────────────────────────────
function buildCredentialLine(credentials, previewUrl) {
  // VaultAcc: full log content in credentials.email, nothing else set
  if (credentials?.email && !credentials?.password && !credentials?.extra) {
    const parts = [credentials.email];
    if (previewUrl) parts.push(previewUrl);
    return parts.join(":");
  }

  // DB: assemble from individual fields
  const parts = [];
  if (credentials?.email)    parts.push(credentials.email);
  if (credentials?.password) parts.push(credentials.password);
  if (credentials?.extra)    parts.push(credentials.extra);
  if (previewUrl)            parts.push(previewUrl);
  return parts.join(":");
}

export default function MyPurchasesDrawer({ onClose }) {
  const [purchases, setPurchases] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [copied,    setCopied]    = useState({});

  const [tutorials,        setTutorials]        = useState([]);
  const [showTutorials,    setShowTutorials]    = useState(false);
  const [loadingTutorials, setLoadingTutorials]  = useState(true);

  useEffect(() => {
    fetchPurchases();
    fetchTutorials();
  }, []);

  const fetchTutorials = async () => {
    setLoadingTutorials(true);
    try {
      const res = await api.get("/social/login-tutorials");
      if (res.data.success) setTutorials(res.data.tutorials || []);
    } catch {
      setTutorials([]); // silent — tutorials are a nice-to-have, never block the drawer
    } finally {
      setLoadingTutorials(false);
    }
  };

  const openTutorial = (url) => window.open(url, "_blank", "noopener,noreferrer");

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const res = await api.get("/social/my-purchases");
      if (res.data.success) setPurchases(res.data.purchases || []);
    } catch {
      toast.error("Failed to load purchases");
    } finally {
      setLoading(false);
    }
  };

  const copyLine = (id, line) => {
    navigator.clipboard.writeText(line).then(() => {
      setCopied(prev => ({ ...prev, [id]: true }));
      setTimeout(() => setCopied(prev => ({ ...prev, [id]: false })), 2000);
    });
  };

  // Group purchases by title + category — same for both DB and VaultAcc
  const grouped = purchases.reduce((acc, p) => {
    const key = `${p.title}__${p.category}`;
    if (!acc[key]) acc[key] = { title: p.title, category: p.category, items: [] };
    acc[key].items.push(p);
    return acc;
  }, {});

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white dark:bg-gray-900 shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <Package size={16} className="text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h2 className="text-sm font-bold text-gray-900 dark:text-white">My Purchases</h2>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {purchases.length} account{purchases.length !== 1 ? "s" : ""} purchased
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={fetchPurchases} disabled={loading}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              <RefreshCw size={15} className={`text-gray-400 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition">
              <X size={18} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Login Tutorials — beeping capsule, admin-managed */}
        {!loadingTutorials && tutorials.length > 0 && (
          <div className="px-5 pt-4 flex-shrink-0">
            <button
              onClick={() => setShowTutorials(v => !v)}
              className="w-full flex items-center gap-2.5 px-3.5 py-2 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-full transition hover:bg-green-100 dark:hover:bg-green-900/30"
            >
              <span className="relative flex-shrink-0 flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" />
              </span>
              <span className="flex-1 text-left text-xs font-semibold text-green-700 dark:text-green-400">
                How to login your accounts
              </span>
              {showTutorials
                ? <ChevronUp   size={14} className="text-green-600 dark:text-green-400 flex-shrink-0" />
                : <ChevronDown size={14} className="text-green-600 dark:text-green-400 flex-shrink-0" />
              }
            </button>

            {showTutorials && (
              <div className="mt-2 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden divide-y divide-gray-100 dark:divide-gray-800">
                {tutorials.map((t) => (
                  <button
                    key={t._id}
                    onClick={() => openTutorial(t.url)}
                    className="w-full flex items-center gap-2 px-3.5 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition"
                  >
                    <ExternalLink size={12} className="text-purple-500 dark:text-purple-400 flex-shrink-0" />
                    <span className="text-xs font-medium text-gray-700 dark:text-gray-200 leading-snug">
                      {t.text}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <RefreshCw size={24} className="text-purple-500 animate-spin" />
              <p className="text-sm text-gray-500 dark:text-gray-400">Loading purchases...</p>
            </div>
          ) : purchases.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 px-6 text-center">
              <Package size={40} className="text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">No purchases yet</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                When you buy accounts, they will appear here with their credentials.
              </p>
            </div>
          ) : (
            <div className="p-4 space-y-6">
              {Object.values(grouped).map(({ title, category, items }) => (
                <div key={`${title}__${category}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-base">{CATEGORY_ICONS[category] || "📱"}</span>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">{title}</p>
                      <p className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                        {category} · {items.length} purchased
                      </p>
                    </div>
                  </div>

                  <div className="space-y-2 pl-7">
                    {items.map((purchase, index) => {
                      const rawLine    = buildCredentialLine(purchase.credentials, purchase.previewUrl);
                      const purchaseId = purchase._id || `${title}-${index}`;

                      const parsed = purchase.source === "shopvia"
                        ? formatShopviaCredential(purchase.credentials?.email, purchase.shopviaDescription)
                        : { type: "raw", text: rawLine };

                      const copyText = parsed.type === "labeled" ? credentialRowsToText(parsed.rows) : parsed.text;

                      return (
                        <div key={purchaseId}
                          className="bg-gray-50 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <span className="w-5 h-5 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 text-xs font-bold flex-shrink-0">
                                {index + 1}
                              </span>
                              <span className="text-xs text-gray-400 dark:text-gray-500">
                                {new Date(purchase.createdAt).toLocaleDateString("en-NG", {
                                  day: "numeric", month: "short", year: "numeric",
                                })}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                                <Currency amount={purchase.priceNgn} />
                              </span>
                              <button onClick={() => copyLine(purchaseId, copyText)}
                                className={`flex items-center gap-1 px-2 py-1 text-xs rounded-lg transition ${
                                  copied[purchaseId]
                                    ? "bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400"
                                    : "bg-white dark:bg-gray-900 text-purple-600 dark:text-purple-400 border border-purple-200 dark:border-purple-800 hover:bg-purple-50 dark:hover:bg-purple-900/20"
                                }`}>
                                {copied[purchaseId] ? <CheckCheck size={11} /> : <Copy size={11} />}
                                {copied[purchaseId] ? "Copied!" : "Copy"}
                              </button>
                            </div>
                          </div>

                          <div className="bg-white dark:bg-gray-900 rounded-lg p-2 border border-gray-200 dark:border-gray-700">
                            {parsed.type === "labeled" ? (
                              <div className="space-y-1">
                                {parsed.rows.map((r, i) => (
                                  <p key={i} className="text-xs text-gray-800 dark:text-gray-200 leading-relaxed">
                                    <span className="font-semibold text-gray-500 dark:text-gray-400">{r.label}:</span>{" "}
                                    <span className="font-mono break-all">{r.value}</span>
                                  </p>
                                ))}
                              </div>
                            ) : (
                              <p className="font-mono text-xs text-gray-800 dark:text-gray-200 break-all leading-relaxed">
                                {copyText || <span className="text-gray-400 italic">No credentials stored</span>}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="px-5 py-4 border-t border-gray-100 dark:border-gray-800 flex-shrink-0">
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
            Each line contains: email · password · extra info · profile link
          </p>
        </div>
      </div>
    </>
  );
}